import store from '../store'
import StoreConstants from '../StoreConstants'
import api from './api'
import Auth from '@aws-amplify/auth'
import AccountApi from '../aws/account/AccountApi'
import AccountLockApi from '../aws/accountlock/accountLockApi'
import ChargeApi from '../aws/charge/ChargeApi'
import EventsApi from '../aws/events/eventsApi'
import ImagesApi from '../aws/images/ImagesApi'
import SupportApi from '../aws/support/SupportApi'
import SurveyApi from '../aws/survey/SurveyApi'

export default {

  _applyCredentials(credentials, expiresAt) {
    //used for the new, modular apis
    store.commit(StoreConstants.commits.setCredentials, {
      accessKey: credentials.accessKeyId,
      secretKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: 'us-west-2',
      defaultContentType: 'application/json',
      defaultAcceptType: 'application/json'
    })

    // Reset modular API clients so they pick up fresh credentials on next call
    AccountApi.inited = false
    AccountLockApi.inited = false
    ChargeApi.inited = false
    EventsApi.inited = false
    ImagesApi.inited = false
    SupportApi.inited = false
    SurveyApi.inited = false

    //used for the old monolith api
    api.setCredentials(this, credentials.accessKeyId,
      credentials.secretAccessKey,
      credentials.sessionToken,
      expiresAt
    )
  },

  async appLogin(accountId, deviceUUID, domain, idToken, expiresAt) {
    console.log('appLogin')
    this.idToken = idToken

    store.commit(StoreConstants.commits.setLoggedInEmail, accountId)
    store.commit(StoreConstants.commits.setDeviceUUID, deviceUUID)

    // Use Amplify's federatedSignIn to exchange the native app's token for Identity Pool credentials.
    // Retry on rate-limit errors (TooManyRequestsException) since the Cognito Identity GetId call
    // can be throttled when many users authenticate concurrently.
    const credentials = await this._federatedSignInWithRetry(domain, idToken, expiresAt, accountId)

    this._applyCredentials(credentials, expiresAt)
    console.log('appLogin done')
  },

  async _federatedSignInWithRetry(domain, idToken, expiresAt, accountId, retries = 2, delayMs = 2000) {
    try {
      return await Auth.federatedSignIn(
        domain,
        { token: idToken, expires_at: expiresAt / 1000 },
        { name: accountId }
      )
    } catch (err) {
      const errStr = err && (err.message || err.toString() || '')
      const isRateLimited = errStr.indexOf('TooManyRequestsException') !== -1 ||
        errStr.indexOf('Rate exceeded') !== -1 ||
        errStr.indexOf('Too Many Requests') !== -1

      if (isRateLimited && retries > 0) {
        console.log(`Auth.federatedSignIn() rate limited, retrying in ${delayMs}ms (${retries} retries left)`)
        await new Promise(resolve => setTimeout(resolve, delayMs))
        return this._federatedSignInWithRetry(domain, idToken, expiresAt, accountId, retries - 1, delayMs)
      }

      throw err
    }
  },
  async setCurrentSession(session) {

    let expiresAt = session.idToken.payload.exp * 1000
    this.expires_at = expiresAt

    console.log(`expires at ${new Date(this.expires_at).toLocaleString()} (${expiresAt}ms)`)

    this.idToken = session.idToken.jwtToken

    let email = session.idToken.payload.email.trim().toLowerCase()

    store.commit(StoreConstants.commits.setLoggedInEmail, email)

    // Get credentials from Amplify (uses the current session's ID token automatically).
    // When a user returns to a page with an expired session, Auth.currentAuthenticatedUser()
    // triggers a token refresh which dispatches a Hub 'tokenRefresh' event. Amplify's
    // Credentials class listens for this and fires off its own GetId call to Cognito Identity.
    // Our Auth.currentCredentials() call below triggers a second independent GetId call because
    // the two code paths (Hub-triggered set() vs explicit get()) don't share a dedup guard.
    // This can cause TooManyRequestsException. On failure, we retry after a delay — by then
    // the Hub-triggered refresh has cached credentials, so _keepAlive() returns them instantly.
    const rawCredentials = await this._getCurrentCredentialsWithRetry()
    const credentials = Auth.essentialCredentials(rawCredentials)

    this._applyCredentials(credentials, this.expires_at)
  },

  async _getCurrentCredentialsWithRetry(retries = 2, delayMs = 2000) {
    try {
      return await Auth.currentCredentials()
    } catch (err) {
      const errStr = err && (err.message || err.toString() || '')
      const isRateLimited = errStr.indexOf('TooManyRequestsException') !== -1 ||
        errStr.indexOf('Rate exceeded') !== -1 ||
        errStr.indexOf('Too Many Requests') !== -1

      if (isRateLimited && retries > 0) {
        console.log(`Auth.currentCredentials() rate limited, retrying in ${delayMs}ms (${retries} retries left)`)
        await new Promise(resolve => setTimeout(resolve, delayMs))
        return this._getCurrentCredentialsWithRetry(retries - 1, delayMs)
      }

      throw err
    }
  },

  getIdToken() {
    return this.idToken
  },

  async logoutAwait(reason, returnTo) {

    store.commit(StoreConstants.commits.setLoggedInEmail, null)

    await Auth.signOut({
      global: false
    })

    if(returnTo) {
      window.location = returnTo
    }
  },
  isLoggedIn() {
    let idToken = this.getIdToken()
    return idToken !== null && idToken !== undefined
  }
}
